import type { StructuredFormData } from "@/types/form";
import {
  formatDateTime,
  formatDateTimeShort,
  formatDisplayText,
} from "./format";

function toRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return {};
  }
  return value as Record<string, unknown>;
}

function toOptionalRecord(value: unknown): Record<string, unknown> | null {
  const record = toRecord(value);
  return Object.keys(record).length > 0 ? record : null;
}

function toRecordArray(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (entry): entry is Record<string, unknown> =>
      typeof entry === "object" && entry !== null && !Array.isArray(entry)
  );
}

function readString(value: unknown) {
  return typeof value === "string" ? value : undefined;
}

function readNonEmptyString(value: unknown) {
  const resolved = readString(value);
  if (!resolved) {
    return undefined;
  }

  const trimmedValue = resolved.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

function readDate(value: unknown) {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "number") {
    return new Date(value);
  }

  const rawValue = readNonEmptyString(value);
  if (!rawValue) {
    return null;
  }

  return new Date(rawValue);
}

function resolveTitle(
  title: string,
  existingTitle: string | undefined,
  fallbackTitle: string
) {
  const trimmedTitle = title.trim();
  if (trimmedTitle.length > 0) {
    return trimmedTitle;
  }

  if (existingTitle && existingTitle.trim().length > 0) {
    return existingTitle;
  }

  return fallbackTitle;
}

function pickFirstString(...values: unknown[]) {
  for (const value of values) {
    const resolved = readNonEmptyString(value);
    if (resolved) {
      return resolved;
    }
  }

  return undefined;
}

/**
 * Normalise form data into a structured format backed by block-based storage.
 */
export function normaliseFormData(
  form: { data: unknown } | undefined
): StructuredFormData {
  const raw = toRecord(form?.data);
  const blocks = toRecord(raw.blocks);
  const meta = toRecord(blocks.meta);
  const context = toRecord(blocks.context);
  const people = toRecordArray(blocks.people);
  const hazards = toRecordArray(blocks.hazards);
  const authorisation = toOptionalRecord(blocks.authorisation);
  const monitoring = toOptionalRecord(blocks.monitoring);

  return {
    data: raw,
    blocks,
    meta,
    context,
    people,
    hazards,
    authorisation,
    monitoring,
  };
}

export function buildEditableFormPayload({
  structured,
  title,
  description,
  dynamicFields,
  fallbackTitle = "Form request",
}: {
  structured: StructuredFormData;
  title: string;
  description: string;
  dynamicFields: Record<string, string>;
  fallbackTitle?: string;
}) {
  const blocks = structured.blocks ?? {};
  const currentMeta = toRecord(blocks.meta);
  const currentContext = toRecord(blocks.context);
  const existingTitle = readString(currentMeta.title);

  const basePayload = {
    ...structured.data,
    blocks: {
      ...blocks,
      meta: {
        ...currentMeta,
        title: resolveTitle(title, existingTitle, fallbackTitle),
      },
      context: {
        ...currentContext,
        description: description.trim(),
      },
    },
  };

  const structuralBlocks = Object.fromEntries(
    Object.entries(structured.blocks).filter(
      ([key]) => key !== "meta" && key !== "context"
    )
  );

  const baseBlocks = basePayload.blocks as Record<string, unknown>;
  return {
    ...basePayload,
    blocks: {
      ...baseBlocks,
      ...structuralBlocks,
      meta: {
        ...((baseBlocks.meta as Record<string, unknown>) || {}),
      },
      context: {
        ...((baseBlocks.context as Record<string, unknown>) || {}),
        ...dynamicFields,
      },
    },
  };
}

export function getAuthorisationDetails(structured: StructuredFormData) {
  const authorisation = structured.authorisation;
  if (!authorisation) {
    return null;
  }

  return {
    level: readNonEmptyString(authorisation.level) ?? "Not specified",
    notes: readNonEmptyString(authorisation.notes) ?? null,
    approvedByUserId:
      readNonEmptyString(authorisation.approved_by_user_id) ?? null,
  };
}

export function getMonitoringDetails(structured: StructuredFormData) {
  const monitoring = structured.monitoring;
  if (!monitoring) {
    return null;
  }

  const rawUpdates = Array.isArray(monitoring.updates)
    ? monitoring.updates
    : [];
  const updates = rawUpdates.flatMap((update, index) => {
    const record = toOptionalRecord(update);
    if (!record) {
      return [];
    }

    return [
      {
        key: `update-${index}-${readNonEmptyString(record.at) ?? "no-date"}`,
        note: readNonEmptyString(record.note) ?? "",
        at: readDate(record.at),
      },
    ];
  });

  const nextCheck = readDate(monitoring.next_check);
  const closedAt = readDate(monitoring.closed_at);

  if (updates.length === 0 && !nextCheck && !closedAt) {
    return null;
  }

  return {
    updates,
    nextCheck,
    closedAt,
  };
}

export function getRejectionDetails(
  form:
    | {
        rejectedAt?: unknown;
        rejectedBy?: unknown;
        rejectionReason?: unknown;
        rejectionMessage?: unknown;
      }
    | null
    | undefined
) {
  const rejectedAt = readDate(form?.rejectedAt);
  const rejectedByUserId = readNonEmptyString(form?.rejectedBy) ?? null;
  const reason = readNonEmptyString(form?.rejectionReason);
  const message = readNonEmptyString(form?.rejectionMessage) ?? null;

  if (!rejectedAt && !rejectedByUserId && !reason && !message) {
    return null;
  }

  return {
    rejectedAt,
    rejectedByUserId,
    reason: reason ?? "No rejection reason recorded",
    message,
  };
}

export function getFormDisplayValues({
  form,
  structured,
  templateName,
}: {
  form:
    | {
        status?: unknown;
        approvedAt?: unknown;
        createdAt?: unknown;
        updatedAt?: unknown;
        id?: unknown;
        siteId?: unknown;
      }
    | null
    | undefined;
  structured: StructuredFormData;
  templateName?: unknown;
}) {
  const status = readNonEmptyString(form?.status) ?? "";
  const statusText = status ? formatDisplayText(status) : "";

  const rawFormType = readNonEmptyString(structured.meta.form_type);
  const formType = rawFormType ? formatDisplayText(rawFormType) : "";
  const templateId = readNonEmptyString(structured.meta.template_id) ?? "";
  const title = pickFirstString(structured.meta.title, templateName) ?? "Form";
  const location = readNonEmptyString(structured.context.location);
  const description = readNonEmptyString(structured.context.description);

  const startTime = readDate(structured.context.start_time);
  const endTime = readDate(structured.context.end_time);

  const scheduleText =
    startTime && endTime
      ? `${formatDateTimeShort(startTime)} - ${formatDateTimeShort(endTime)}`
      : startTime
        ? `From ${formatDateTimeShort(startTime)}`
        : endTime
          ? `Until ${formatDateTimeShort(endTime)}`
          : "Not scheduled";

  const createdAt = readDate(form?.createdAt);
  const updatedAt = readDate(form?.updatedAt);
  const approvedAt = readDate(form?.approvedAt);

  return {
    statusText,
    status,
    formType,
    templateId,
    title,
    location: location ?? "Not specified",
    description: description ?? "No description supplied",
    scheduleText,
    createdAtText: createdAt ? formatDateTime(createdAt) : null,
    updatedAtText: updatedAt ? formatDateTime(updatedAt) : null,
    approvedAtText: approvedAt ? formatDateTime(approvedAt) : null,
    hasSchedule: !!(startTime || endTime),
    formId: readNonEmptyString(form?.id) ?? "",
    siteId: readNonEmptyString(form?.siteId) ?? "",
  };
}
